import { useState, type FormEvent, type RefObject } from 'react';
import type { EquippedSlot } from '../api/wardrobe';
import { useSaveLook, useDeleteLook, useLooks, type LookDto } from '../api/looks';
import { ApiClientError } from '../api/client';
import { FormField } from '../screens/FormField';
import { deriveFormError } from '../screens/formErrors';
import { validateName } from '../companion/nameValidation';
import { BottomSheet, Dialog } from '../components/BottomSheet';

export type SaveLookSheetProps = {
  companionId: number;
  /** The current draft equipped-slots map, saved as-is — this sheet never re-derives or
   *  filters it. (The one wire-level exception lives in api/looks.ts's `saveLook`, not here:
   *  `projected: true` transmog entries are stripped from every outgoing slot map — see
   *  `stripProjected`'s doc comment in api/wardrobe.ts.) */
  draft: Record<string, EquippedSlot>;
  onClose: () => void;
  /** Fired once a save (plain or overwrite) actually succeeds. WardrobeScreen owns what happens
   *  next — clearing `dirty`, the "Look saved" toast, closing this sheet, and (Task 16) firing
   *  the install-prompt trigger — none of that is this component's job. */
  onSaved: (look: LookDto) => void;
  /** The "Save Look" button that opened this sheet — see BottomSheet.tsx's own doc comment on
   *  why this is needed for reliable focus-return. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /** Gallery-only — see BottomSheet.tsx's own doc comment. */
  portalContainer?: HTMLElement | null;
};

const LOOK_CAP = 10;

/**
 * SCREENS.md "save-look-sheet": a bottom sheet with a Look name field. Under the 10-Look cap,
 * that's the whole form. At the cap (SCREENS.md: "Look limit reached, delete or overwrite"),
 * the existing Looks list appears below the name field so the player can free up a slot: Delete
 * (with an inline tap-to-confirm step, since there's no modal-confirm primitive in this app) or
 * Overwrite (delete that Look, then save the current name+draft as a new one — sequenced, not
 * parallel, so a failed delete never fires the save). `forcedCap` covers the race where the
 * cached `useLooks` count was momentarily stale (<10 locally) but the server still rejects a
 * plain save with `look_limit` — the cap UI takes over from that response, not just the client's
 * own count.
 *
 * Built on `BottomSheet` (`@radix-ui/react-dialog` — FABLE5_BUILD_PROMPT.md §3), which supplies
 * the focus trap, Escape-to-close, and overlay-click-to-close.
 */
export function SaveLookSheet({
  companionId, draft, onClose, onSaved, restoreFocusRef, portalContainer,
}: SaveLookSheetProps) {
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const [forcedCap, setForcedCap] = useState(false);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<number | null>(null);
  // IMPORTANT (reviewer finding, fix round): overwrite's delete-then-save is two SEPARATE
  // requests. If the delete succeeds but the save that follows fails, the player's old Look is
  // genuinely gone — a generic "something went wrong" would leave them thinking nothing
  // happened, when actually they're down a Look with no new one to show for it. This tracks
  // exactly that state so the copy can say so honestly.
  const [overwriteDeletedButSaveFailed, setOverwriteDeletedButSaveFailed] = useState(false);

  const looksQuery = useLooks(companionId);
  const saveLook = useSaveLook();
  const deleteLook = useDeleteLook();

  const localNameError = validateName(name);
  const activeError = saveLook.error ?? deleteLook.error;
  const { fieldErrors, formError } = deriveFormError(activeError);
  const nameError = (touched ? localNameError : undefined) ?? fieldErrors.name;
  // `look_limit` already has its own explanation (the cap view's own note, below) — showing the
  // generic fallback formError too would just repeat "Look limit reached" twice on screen.
  // Any OTHER error while in the cap view (e.g. a delete/overwrite failing for some other
  // reason) still needs to surface here.
  const isLookLimitError = activeError instanceof ApiClientError && activeError.code === 'look_limit';

  const looks = looksQuery.data ?? [];
  const atCap = looks.length >= LOOK_CAP || forcedCap;
  const busy = saveLook.isPending || deleteLook.isPending;

  async function save() {
    const look = await saveLook.mutateAsync({ companionId, name, slots: draft });
    onSaved(look);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    setOverwriteDeletedButSaveFailed(false); // a fresh (non-overwrite) attempt supersedes it
    if (localNameError || busy) return;
    try {
      await save();
    } catch (err) {
      // A stale local count (still < 10) but the server disagrees: fall back to the cap UI
      // instead of leaving the player staring at a generic form error with no way forward.
      if (err instanceof ApiClientError && err.code === 'look_limit') setForcedCap(true);
    }
  }

  async function handleOverwrite(oldLookId: number) {
    setTouched(true);
    setOverwriteDeletedButSaveFailed(false);
    if (localNameError || busy) return;
    // Sequenced, not Promise.all — a failed delete must never be followed by a save (that would
    // leave the player with 11 Looks, silently over the cap from their perspective). Two
    // separate try/catches (not one wrapping both awaits) so this can tell "delete itself
    // failed" (nothing changed, the old Look is safe, the generic formError already covers it)
    // apart from "delete succeeded but save failed" (the old Look is genuinely gone — distinct
    // copy below).
    try {
      await deleteLook.mutateAsync(oldLookId);
    } catch {
      return;
    }
    try {
      await save();
    } catch {
      setOverwriteDeletedButSaveFailed(true);
    }
  }

  async function handleDeleteClick(lookId: number) {
    if (confirmingDeleteId !== lookId) {
      setConfirmingDeleteId(lookId);
      return;
    }
    try {
      await deleteLook.mutateAsync(lookId);
      setConfirmingDeleteId(null);
      setForcedCap(false); // may now be back under the cap — let the real count take over
    } catch {
      // Surfaced via formError above.
    }
  }

  return (
    <BottomSheet
      open
      onOpenChange={(next) => { if (!next) onClose(); }}
      contentClassName="save-look-sheet"
      restoreFocusRef={restoreFocusRef}
      portalContainer={portalContainer}
    >
      <div className="sheet-header">
        <Dialog.Title asChild>
          <h2>Save Look</h2>
        </Dialog.Title>
        <Dialog.Close asChild>
          <button type="button" className="sheet-close" aria-label="Close save Look sheet">
            Close
          </button>
        </Dialog.Close>
      </div>
      <Dialog.Description className="sr-only">
        Name your current outfit and save it as a Look.
      </Dialog.Description>

      <form className="sheet-body" onSubmit={handleSubmit}>
        <FormField
          label="Look name"
          minLength={2}
          maxLength={20}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setTouched(true)}
          disabled={busy}
          error={nameError}
        />
        {overwriteDeletedButSaveFailed ? (
          <p className="field-error" role="alert">
            Your old Look was removed but the new one didn&rsquo;t save — your outfit is still on,
            try saving again.
          </p>
        ) : (
          formError && !isLookLimitError && <p className="field-error" role="alert">{formError}</p>
        )}

        {atCap ? (
          <div className="look-cap">
            <p className="form-note">
              Look limit reached — delete a Look to save a new one, or overwrite one below.
            </p>
            <ul className="look-cap-list">
              {looks.map((look) => (
                <li key={look.id} className="look-cap-row">
                  <span className="look-cap-name">{look.name}</span>
                  <button
                    type="button"
                    className="btn-quiet look-cap-overwrite"
                    disabled={busy || !!localNameError}
                    onClick={() => handleOverwrite(look.id)}
                  >
                    Overwrite
                  </button>
                  <button
                    type="button"
                    className="btn-destructive-quiet look-cap-delete"
                    disabled={busy}
                    onClick={() => handleDeleteClick(look.id)}
                  >
                    {confirmingDeleteId === look.id ? 'Confirm delete' : 'Delete'}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="actions">
            <button type="submit" className="btn-primary" disabled={busy || looksQuery.isLoading}>
              {saveLook.isPending ? 'Saving…' : 'Save Look'}
            </button>
          </div>
        )}
      </form>
    </BottomSheet>
  );
}
